import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Directive, TemplateRef, ViewEncapsulation, computed, contentChild, effect, inject, input, linkedSignal, output, signal, untracked } from '@angular/core';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { KanbanColumn, KanbanDbClickParams, KanbanEditorType, KanbanHost, KanbanSwimlane } from '../model/kanban.model';
import { KanbanSwimlaneComponent } from '../swimlane/kanban.swimlane.component';
import { kanbanBridgeStyles, kanbanStyleId } from './kanban.theme';
import { ColorUtility } from '../utilities/kanban.utilities';
import { buildColumns, buildGridColumns, calculateColumnWidth, carryCollapseState, syncSelection } from '../utilities/kanban.state.utilities';

@Directive({
  selector: '[swimlaneHeaderTemplate]'
})
export class KanbanSwimlaneHeaderTemplateDirective { }

@Directive({
  selector: '[externalEditorTemplate]'
})
export class KanbanExternalEditorTemplateDirective { }

@Directive({
  selector: '[swimlaneColumnTemplate]'
})
export class KanbanSwimlaneColumnTemplateDirective { }

@Directive({
  selector: '[itemTemplate]'
})
export class KanbanItemTemplateDirective { }

@Directive({
  selector: '[columnTemplate]'
})
export class KanbanColumnTemplateDirective { }

@Directive({
  selector: '[footerTemplate]'
})
export class KanbanFooterTemplateDirective { }

@Directive({
  selector: '[editorTemplate]'
})
export class KanbanEditorTemplateDirective { }

@Directive({
  selector: '[swimlaneEditorTemplate]'
})
export class KanbanSwimlaneEditorTemplateDirective { }

@Component({
  selector: 'kanban, w-kanban',
  templateUrl: './kanban.component.html',
  styleUrls: ['./kanban.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, KanbanSwimlaneComponent],
  host: {
    '[class.has-background]': '!!$backgroundImage()',
    '[style.background-image]': '$backgroundImageStyle()',
    '[style.--kanban-grid-columns]': '$gridColumns()',
  }
})
export class KanbanComponent implements KanbanHost {
  private readonly document = inject(DOCUMENT);

  public readonly KanbanEditorType = KanbanEditorType;
  public readonly colorUtility = new ColorUtility();
  public readonly host: KanbanHost = this;

  public readonly $backgroundImage = input('', { alias: 'backgroundImage' });
  public readonly $showHeader = input(true, { alias: 'showHeader' });
  public readonly $showSwimlaneColumn = input(false, { alias: 'showSwimlaneColumn' });
  public readonly showSwimlaneColumn = this.$showSwimlaneColumn;
  public readonly $swimlaneColumnHeader = input('Swimlane Title', { alias: 'swimlaneColumnHeader' });
  public readonly $readonly = input(false, { alias: 'readonly' });
  public readonly $editorWidth = input('450px', { alias: 'editorWidth' });
  public readonly $emptyMessage = input('Empty', { alias: 'emptyMessage' });
  public readonly emptyMessage = this.$emptyMessage;
  public readonly $valueField = input('', { alias: 'valueField' });
  public readonly valueField = this.$valueField;
  /**
   * Row identity, when a flat field name cannot express it.
   *
   * An unwound board's rows share the root `_id` — every stage of one job carries the job's id — so
   * `valueField` would make selecting one stage select all of its siblings. Callers whose rows are
   * parent-shaped pass their own resolver; everyone else keeps the field lookup.
   */
  public readonly $itemKey = input<((item: any) => string) | null>(null, { alias: 'itemKey' });
  public readonly $editorType = input(KanbanEditorType.Internal, { alias: 'editorType' });
  public readonly $canUnselectedItem = input(true, { alias: 'canUnselectedItem' });
  public readonly $selectedItemInput = input<any | null | undefined>(undefined, { alias: 'selectedItem' });
  public readonly $swimlanesInput = input<KanbanSwimlane[]>([], { alias: 'swimlanes' });

  public readonly itemSelected = output<any>();
  public readonly swimlaneItemSelected = output<any>();
  public readonly itemDblClicked = output<KanbanDbClickParams>();

  public readonly $externalEditorTemplate = contentChild(KanbanExternalEditorTemplateDirective, { read: TemplateRef });
  public readonly externalEditorTemplate = this.$externalEditorTemplate;
  public readonly $swimlaneHeaderTemplate = contentChild(KanbanSwimlaneHeaderTemplateDirective, { read: TemplateRef });
  public readonly swimlaneHeaderTemplate = this.$swimlaneHeaderTemplate;
  public readonly $swimlaneColumnTemplate = contentChild(KanbanSwimlaneColumnTemplateDirective, { read: TemplateRef });
  public readonly swimlaneColumnTemplate = this.$swimlaneColumnTemplate;
  public readonly $columnTemplate = contentChild(KanbanColumnTemplateDirective, { read: TemplateRef });
  public readonly columnTemplate = this.$columnTemplate;
  public readonly $itemTemplate = contentChild(KanbanItemTemplateDirective, { read: TemplateRef });
  public readonly itemTemplate = this.$itemTemplate;
  public readonly $editorTemplate = contentChild(KanbanEditorTemplateDirective, { read: TemplateRef });
  public readonly editorTemplate = this.$editorTemplate;
  public readonly $swimlaneEditorTemplate = contentChild(KanbanSwimlaneEditorTemplateDirective, { read: TemplateRef });
  public readonly swimlaneEditorTemplate = this.$swimlaneEditorTemplate;
  public readonly $footerTemplate = contentChild(KanbanFooterTemplateDirective, { read: TemplateRef });
  public readonly footerTemplate = this.$footerTemplate;

  /**
   * Lanes, with the operator's collapse answers carried across a new input array.
   *
   * Collapse lives here rather than in the source data, so a caller that re-emits its lanes — a
   * board laying one saved record over its page — would otherwise silently re-expand every lane and
   * column the operator had closed. Carrying it forward by KEY is what makes an in-place card update
   * invisible to everything except the card itself.
   */
  public readonly $swimlanesState = linkedSignal<KanbanSwimlane[], KanbanSwimlane[]>({
    source: () => this.$swimlanesInput(),
    computation: (lanes, previous) => (previous ? carryCollapseState(lanes, previous.value) : lanes),
  });
  public readonly $swimlanes = this.$swimlanesState.asReadonly();
  public readonly $columns = computed(() => buildColumns(this.$swimlanesState()));
  public readonly columns = this.$columns;
  public readonly $columnWidth = computed(() => calculateColumnWidth(this.$columns(), this.$showSwimlaneColumn()));
  public readonly columnWidth = this.$columnWidth;
  public readonly $gridColumns = computed(() => buildGridColumns(this.$columns(), this.$showSwimlaneColumn()));
  public readonly $selectedItemState = signal<any>(null);
  public readonly selectedItem = this.$selectedItemState.asReadonly();
  public readonly $backgroundImageStyle = computed(() => this.$backgroundImage() ? `url('${this.$backgroundImage()}')` : null);

  public constructor() {
    DesignSystem.Mount(this.document, kanbanStyleId, kanbanBridgeStyles);

    effect(() => {
      const selectedItem = this.$selectedItemInput();

      if (selectedItem === undefined) {
        return;
      }

      const lanes = untracked(() => this.$swimlanesState());
      const syncedSelection = syncSelection(lanes, selectedItem, this.$valueField(), this.$itemKey());

      // Writing the same lanes back would still notify `$swimlanesState`'s consumers — `$columns`,
      // `$gridColumns` and every lane's template — so only write when a lane actually expanded.
      if (syncedSelection.lanes !== lanes) {
        this.$swimlanesState.set(syncedSelection.lanes);
      }

      this.$selectedItemState.set(selectedItem ?? null);
    });

  }

  public getItems(lane: KanbanSwimlane, columnKey: string): any[] {
    return lane.columns.find(column => column.key === columnKey)?.items ?? [];
  }

  public hasItems(columnKey: string): boolean {
    return this.$swimlanesState().some(lane => lane.columns.some(column => column.key === columnKey && (column.items?.length ?? 0) > 0));
  }

  public laneColumnHasItems(lane: KanbanSwimlane, columnKey: string): boolean {
    return lane.columns.some(column => column.key === columnKey && (column.items?.length ?? 0) > 0);
  }

  public columnItemCount(columnKey: string): number {
    return this.$swimlanesState().reduce((count, lane) => count + lane.columns.filter(column => column.key === columnKey).reduce((laneCount, column) => laneCount + (column.items?.length ?? 0), 0), 0);
  }

  /**
   * Selection is applied on the click itself, NOT deferred behind a double-click discriminator.
   *
   * This used to sit behind a 250ms timeout so a double click could cancel the pending single click.
   * Highlighting a card is pure local state, so that quarter-second was a visible lag on every single
   * click — the whole cost of the interaction.
   *
   * A double click still arrives as click, click, dblclick. Only the FIRST click may toggle: the
   * second carries `detail === 2` and is skipped, or it would unselect the card the double click is
   * about to open. `detail` is 0 for synthetic clicks (keyboard activation), which must still toggle.
   */
  public onItemSelected(item: any, swimlane = false, event?: MouseEvent): void {
    if ((event?.detail ?? 0) > 1) {
      return;
    }

    this.applySelection(item, swimlane);
  }

  /**
   * Selection from a pointer gesture.
   *
   * `wbPointerGestures` has already decided single from double, so the `detail > 1` guard that
   * `onItemSelected` needs would never fire here — the second tap of a double tap arrives as
   * `doubleTap`, not as a second `tap`. The semantic is the same as before: exactly one toggle per
   * double gesture, and the card the gesture opens stays selected.
   */
  public onItemTapped(item: any, swimlane = false): void {

    this.applySelection(item, swimlane);
  }

  private applySelection(item: any, swimlane: boolean): void {

    const currentItem = this.$selectedItemState();
    const nextItem = this.$canUnselectedItem() && currentItem === item ? null : item;

    this.$selectedItemState.set(nextItem);

    if (swimlane) {
      this.swimlaneItemSelected.emit(nextItem);
      return;
    }

    this.itemSelected.emit(nextItem);
  }

  /** Asserts the selection rather than toggling it, so opening a selected card never deselects it. */
  public onItemDblClicked(event: KanbanDbClickParams): void {
    this.$selectedItemState.set(event.item);
    this.itemDblClicked.emit(event);
  }

  public onColumnToggle(column: KanbanColumn): void {
    const collapsed = !column.collapsed;

    this.$swimlanesState.update(lanes => lanes.map(lane => ({
      ...lane,
      columns: lane.columns.map(currentColumn => currentColumn.key === column.key ? { ...currentColumn, collapsed } : currentColumn)
    })));
  }

  public canCollapse(column: KanbanColumn): boolean {
    const otherColumnsAreCollapsed = this.$columns().filter(currentColumn => currentColumn.key !== column.key).every(currentColumn => !!currentColumn.collapsed);
    return !otherColumnsAreCollapsed || !!column.collapsed;
  }

  public onLaneToggle(lane: KanbanSwimlane): void {
    this.$swimlanesState.update(lanes => lanes.map(currentLane => currentLane === lane ? { ...currentLane, collapsed: !currentLane.collapsed } : currentLane));
  }

  public canCollapseLane(lane: KanbanSwimlane): boolean {
    const otherLanesAreCollapsed = this.$swimlanesState().filter(currentLane => currentLane !== lane).every(currentLane => !!currentLane.collapsed);
    return !otherLanesAreCollapsed || !!lane.collapsed;
  }

  public laneItemCount(lane: KanbanSwimlane): number {
    return lane.columns.reduce((count, column) => count + (column.items?.length ?? 0), 0);
  }

  /**
   * Card identity for `@for`.
   *
   * `itemKey` is the caller's row identity, so a card whose RECORD was replaced in place — a
   * quickview save laying a confirmed record over the page — keeps the same key and therefore the
   * same DOM node, instead of being destroyed and re-created under the operator's cursor. Boards
   * with no resolver fall back to the composite id the column template used before.
   */
  public trackItem(item: any, index: number): unknown {

    const itemKey = this.$itemKey();
    if (itemKey && item) return itemKey(item);

    return `${item?._id ?? ''}-${item?.workOrders?._id ?? index}`;
  }

  public isSelected(item: any, selectedItem: any): boolean {
    const itemKey = this.$itemKey();

    if (itemKey) {
      if (!item || !selectedItem) return false;
      return itemKey(item) === itemKey(selectedItem);
    }

    const valueField = this.$valueField();

    if (!valueField) {
      return item === selectedItem;
    }

    const itemId = item?.[valueField] ?? '-1';
    const selectedItemId = selectedItem?.[valueField] ?? '-2';
    return itemId === selectedItemId;
  }
}
